import {Component, AfterViewInit, ViewChild, ElementRef} from '@angular/core';
import {BaseComponent} from '../../../base/base.component';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
    RegisterDaycareEntryGQL,
    RegisterDaycareExitGQL,
    SearchDaycarePickupCandidatesGQL,
    User
} from '../../../../generated/graphql';
import {UserService} from '../../../service/user.service';

/**
 * PORTARIA da Creche (Fatia 6 + Fatia 8B — §47.1/§47.5). Usada SOB PRESSÃO: campo
 * único, foco automático, Enter busca. NUNCA bloqueia — só mostra candidatos, o
 * ESTADO de cada criança (dentro/fora) e registra a decisão; quem decide é o
 * vigilante. Fatia 8: DUAS AÇÕES — o vigilante escolhe ENTRADA ou SAÍDA primeiro,
 * depois busca (evita seleção com estados mistos). Avisos NÃO-bloqueantes: se o
 * back devolver `requiresConfirmation`, mostra o `warning` dele e, se o vigilante
 * confirmar, rechama a mutation com `confirmWarnings: true`.
 */
@Component({
    selector: 'app-daycare-gate',
    templateUrl: './daycare-gate.component.html',
    styleUrls: ['./daycare-gate.component.scss']
})
export class DaycareGateComponent extends BaseComponent implements AfterViewInit {

    @ViewChild('searchInput', {static: false}) searchInput: ElementRef;

    user = new User();
    term = '';
    // Ação escolhida ANTES da busca (a tela muda de comportamento).
    mode: 'entry' | 'exit' = 'entry';
    candidates: Array<any> = [];
    searched = false;
    loading = false;

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private searchGQL: SearchDaycarePickupCandidatesGQL,
                private entryGQL: RegisterDaycareEntryGQL,
                private exitGQL: RegisterDaycareExitGQL
    ) {
        super(router, translate);
    }

    ngAfterViewInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                if (this.actionUserProfile() === false) {
                    super.showMessage(this.translate.instant('atencao'), this.translate.instant('semacesso'), 'warning');
                    this.router.navigate(['/']);
                    return;
                }
                this.focusSearch();
            }, error: err => super.onError(err)
        });
    }

    // Troca a ação (entrada/saída). Limpa os resultados para não misturar estados.
    setMode(mode: 'entry' | 'exit') {
        if (this.mode === mode) { return; }
        this.mode = mode;
        this.candidates = [];
        this.searched = false;
        this.focusSearch();
    }

    focusSearch() {
        setTimeout(() => {
            if (this.searchInput && this.searchInput.nativeElement) {
                this.searchInput.nativeElement.focus();
            }
        });
    }

    search() {
        const t = (this.term || '').trim();
        if (!t) {
            this.candidates = [];
            this.searched = false;
            return;
        }
        this.loading = true;
        this.searchGQL.fetch({term: t}, {fetchPolicy: 'network-only'}).subscribe({
            next: ({data}) => {
                this.candidates = (data.searchDaycarePickupCandidates || []).map(c => ({
                    personKind: c.personKind,
                    personName: c.personName,
                    personDocument: c.personDocument,
                    employeeRe: c.employeeRe,
                    children: this.sortChildren((c.children || []).map(ch => ({
                        id: ch.id, name: ch.name, status: ch.status, entryAt: ch.entryAt, checked: false
                    })))
                }));
                this.searched = true;
                this.loading = false;
            }, error: err => {
                this.loading = false;
                super.onError(err);
            }
        });
    }

    // No modo SAÍDA, quem está DENTRO aparece primeiro (é quem vai sair).
    private sortChildren(children: Array<any>): Array<any> {
        if (this.mode !== 'exit') { return children; }
        return [...children].sort((a, b) => {
            const rank = (s: string) => (s === 'in' ? 0 : 1);
            const byRank = rank(a.status) - rank(b.status);
            return byRank !== 0 ? byRank : (a.name || '').localeCompare(b.name || '');
        });
    }

    get multiplePeople(): boolean {
        return this.candidates.length > 1;
    }

    selectedChildren(cand: any): Array<any> {
        return (cand.children || []).filter(ch => ch.checked);
    }

    // Registro: envia SEM confirmWarnings; se o back pedir confirmação (aviso),
    // mostra o warning DELE e rechama com confirmWarnings=true no "Sim".
    register(cand: any) {
        const selected = this.selectedChildren(cand);
        if (selected.length === 0) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.selecionecrianca'), 'warning');
            return;
        }
        this.doRegister(cand, selected, false);
    }

    private doRegister(cand: any, selected: Array<any>, confirmWarnings: boolean) {
        this.loading = true;
        const input = {
            childIds: selected.map(c => c.id),
            personKind: cand.personKind,
            personName: cand.personName,
            personDocument: cand.personDocument || null,
            employeeRe: cand.employeeRe,
            note: null,
            confirmWarnings
        };
        const gql: any = this.mode === 'entry' ? this.entryGQL : this.exitGQL;
        gql.mutate({data: input}).subscribe({
            next: ({data}) => {
                this.loading = false;
                const res = this.mode === 'entry' ? data.registerDaycareEntry : data.registerDaycareExit;
                if (res && res.requiresConfirmation) {
                    // Aviso NÃO-bloqueante: o texto vem do back (ele explica o caso).
                    super.confirmMessage(this.translate.instant('atencao'), res.warning,
                        () => this.doRegister(cand, selected, true));
                    return;
                }
                super.showMessage(this.translate.instant('success'), res.message, 'success');
                this.reset(); // mantém o modo — a fila continua
            }, error: err => {
                this.loading = false;
                super.onError(err);
            }
        });
    }

    // Limpa e volta o foco ao campo, MANTENDO o modo selecionado.
    reset() {
        this.term = '';
        this.candidates = [];
        this.searched = false;
        this.focusSearch();
    }

    actionUserProfile(): boolean {
        switch (this.user.profile) {
            case 'vigilant':
            case 'vigilant-leader':
            case 'admin':
            case 'local-manager':
                return true;
            default:
                return false;
        }
    }
}
